import React from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { StylesConfig, ValueType } from '@atlaskit/select';

import { useIntl } from '@atlassian/jira-intl';
import { getColorPickerPalette } from '@atlassian/jira-servicedesk-customer-service-common';
import type { Field, SelectFieldOptionsType } from '../../types';
import { SelectFieldView } from '../common';
import { OptionView } from './option-view';
import { MultiSelectFieldReadView } from './read-view';

export const MultiSelectField = ({
	id,
	label,
	values,
	formattedOptions,
	onChange,
	isInline,
	getValueContainer,
}: Field) => {
	const { formatMessage } = useIntl();
	const palette = getColorPickerPalette(formatMessage);
	const selectedOption: SelectFieldOptionsType[] =
		values
			?.map((value) => formattedOptions?.find((option) => option.value === value))
			.filter(Boolean) || [];

	const onSelection = (
		selectedOptions: ValueType<SelectFieldOptionsType, true>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		const selectedValues = selectedOptions?.map((option) => option.value) || [];

		if (values?.join(',') === selectedValues.join(',')) return;

		onChange({ values: selectedValues }, analyticsEvent);
	};

	const selectViewStyles: StylesConfig = {
		multiValueLabel: (base, { data }) => ({
			...base,
			borderTopRightRadius: '0',
			borderBottomRightRadius: '0',
			backgroundColor: palette.find((c) => c.tag === data.color)?.value,
		}),
		multiValueRemove: (base, { data }) => ({
			...base,
			backgroundColor: data.color && palette.find((c) => c.tag === data.color)?.value,
		}),
	};

	return (
		<SelectFieldView
			isMulti
			id={id}
			label={label}
			closeMenuOnSelect={false}
			blurInputOnSelect={false}
			value={selectedOption}
			valueAsString={values?.join(', ')}
			options={formattedOptions}
			styles={selectViewStyles}
			components={{
				Option: OptionView,
			}}
			onSelection={onSelection}
			isInline={isInline}
			readView={() => (
				<MultiSelectFieldReadView
					values={values}
					formattedOptions={formattedOptions}
					getValueContainer={getValueContainer}
					inInlineEdit
				/>
			)}
		/>
	);
};

export { MultiSelectFieldReadView };
